import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Edit, Users, TrendingUp, CheckCircle, Clock, MapPin, Building2, DollarSign, Share } from 'lucide-react';
import { useRoleDetail } from '@/hooks/useRoleDetail';
import { useReferralsByRole } from '@/hooks/useReferralsByRole';
import RoleFormModal from '@/components/admin/RoleFormModal';
import ReferralList from '@/components/referrals/ReferralList';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const RoleDetailPage = () => {
  const { roleId } = useParams<{ roleId: string }>();
  const navigate = useNavigate();
  const { role, isLoading } = useRoleDetail(roleId || '');
  const { referrals, isLoading: referralsLoading } = useReferralsByRole(roleId || '');
  const [showEditModal, setShowEditModal] = useState(false);
  const { toast } = useToast();

  const formatSalaryRange = (salaryMin?: string | number | null, salaryMax?: string | number | null) => {
    const min = typeof salaryMin === 'string' ? parseFloat(salaryMin) : salaryMin;
    const max = typeof salaryMax === 'string' ? parseFloat(salaryMax) : salaryMax;
    
    if (min && max) {
      return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
    } else if (min) {
      return `$${min.toLocaleString()}+`;
    } else if (max) {
      return `Up to $${max.toLocaleString()}`;
    }
    return 'Not specified';
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading role details...</p>
        </div>
      </div>
    );
  }

  if (!role) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Role Not Found</h2>
            <p className="text-gray-600 mb-4">The requested role could not be found.</p>
            <Button onClick={() => navigate('/manage-roles')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Manage Roles
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'destructive';
      case 'medium':
        return 'default';
      case 'low':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center space-x-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/manage-roles')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-3xl font-bold text-gray-900">{role.title}</h1>
                <Badge variant={getPriorityColor(role.priority || 'medium')}>
                  {role.priority} priority
                </Badge>
                <Badge variant={role.is_active ? "default" : "secondary"}>
                  {role.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div className="flex items-center space-x-4 text-gray-600 mt-1">
                <div className="flex items-center space-x-1">
                  <Building2 className="w-4 h-4" />
                  <span>{role.department}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <MapPin className="w-4 h-4" />
                  <span>{role.location}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button onClick={() => navigate(`/referrals/${role.id}`)}>
            <Users className="w-4 h-4 mr-2" />
            View Referrals
          </Button>
          <Button 
            variant="outline" 
            onClick={async () => {
              try {
                // Create a proper public role link using the Edge function
                const { data, error } = await supabase.functions.invoke('create-public-role-link', {
                  body: { roleId: role.id }
                });

                if (error) throw error;

                const publicUrl = `https://sourcerershare.site/${data.publicSlug}`;
                
                // Try to copy to clipboard, with fallback for focus issues
                try {
                  await navigator.clipboard.writeText(publicUrl);
                  toast({
                    title: "Role link copied!",
                    description: "Shareable role link has been copied to your clipboard.",
                  });
                } catch (clipboardError) {
                  // Fallback: show the URL in a dialog or prompt
                  console.warn('Clipboard access failed, showing fallback:', clipboardError);
                  
                  // Create a temporary text area to select the URL
                  const textArea = document.createElement('textarea');
                  textArea.value = publicUrl;
                  textArea.style.position = 'fixed';
                  textArea.style.left = '-999999px';
                  textArea.style.top = '-999999px';
                  document.body.appendChild(textArea);
                  textArea.focus();
                  textArea.select();
                  
                  try {
                    document.execCommand('copy');
                    toast({
                      title: "Role link copied!",
                      description: "Shareable role link has been copied to your clipboard.",
                    });
                  } catch (fallbackError) {
                    // Last resort: show the URL to the user
                    toast({
                      title: "Role link created!",
                      description: `Please copy this link: ${publicUrl}`,
                      duration: 10000,
                    });
                  }
                  
                  document.body.removeChild(textArea);
                }
                
              } catch (error) {
                console.error('Error creating role link:', error);
                toast({
                  title: "Error",
                  description: "Failed to create role link. Please try again.",
                  variant: "destructive",
                });
              }
            }}
          >
            <Share className="w-4 h-4 mr-2" />
            Share this role
          </Button>
          <Button variant="outline" onClick={() => setShowEditModal(true)}>
            <Edit className="w-4 h-4 mr-2" />
            Edit Role
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Referrals</p>
                <p className="text-xl font-bold">{role.total_referrals}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Clock className="w-4 h-4 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-xl font-bold">{role.active_referrals}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Hired</p>
                <p className="text-xl font-bold">{role.hired_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Bonus Amount</p>
                <p className="text-xl font-bold">${parseFloat(role.bonus_amount || '0').toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Current Referrals */}
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-900">Current Referrals</h2>
        {referralsLoading ? (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading referrals...</p>
            </CardContent>
          </Card>
        ) : (
          <ReferralList referrals={referrals || []} roleTitle={role.title} />
        )}
      </div>

      {/* Role Form Modal */}
      <RoleFormModal 
        isOpen={showEditModal} 
        onClose={() => setShowEditModal(false)} 
        editingRole={role} 
      />
    </div>
  );
};

export default RoleDetailPage;
